import React, { useState, useEffect, useMemo, useCallback, forwardRef } from 'react'
import { useDispatch } from 'react-redux'

import PropTypes from 'prop-types'

import differenceWith from 'lodash/difference'
import find from 'lodash/find'
import forEach from 'lodash/forEach'
import isEmpty from 'lodash/isEmpty'
import map from 'lodash/map'
import merge from 'lodash/merge'
import omit from 'lodash/omit'
import toLower from 'lodash/toLower'

import { Scope } from '@galo-monitor/forms'
import breedingSchema from '@galo-monitor/forms/schemas/root/breeding.schema'
import { useT } from '@galo-monitor/i18n'
import useGaloMonitorApi from '@galo-monitor/services/hooks/useGaloMonitorApi'
import { useSnackbar } from '@galo-monitor/snackbar'
import { BreedingActions } from '@galo-monitor/stores/breeding'
import Form from '@galo-monitor/web-components/Form'
import InputColor from '@galo-monitor/web-components/InputColor'
import InputSelect from '@galo-monitor/web-components/InputSelect'
import InputText from '@galo-monitor/web-components/InputText'

import { Container } from './theme'

const countryFieldNames = {
  '1': 'ptBr',
  '2': 'deDe',
  '3': 'esEs',
  '4': 'enUs'
}

const BreedingForm = forwardRef((props, formRef) => {
  const { breeding, loading, setLoading, onSuccess, onError } = props
  const dispatch = useCallback(useDispatch(), [])
  const t = useT()
  const snackbar = useSnackbar()

  const { data: countries } = useGaloMonitorApi('/shared/countries')

  const [breedingNames, setBreedingNames] = useState({})

  const countryOptions = useMemo(
    () => map(countries, ({ name }) => ({
      label: name,
      value: name
    })),
    [countries]
  )

  const getCountryIdByName = useCallback(
    countryName => String(find(
      countries,
      country => country.name === countryName
    )?.id),
    [countries]
  )

  const handleOnCountryChange = useCallback(
    value => {
      const [addKey] = differenceWith(value, Object.keys(breedingNames))
      const [removeKey] = differenceWith(Object.keys(breedingNames), value)

      if (addKey) {
        setBreedingNames(prevState => ({
          ...prevState,
          [addKey]: ''
        }))
      }

      if (removeKey) {
        setBreedingNames(omit(breedingNames, removeKey))
      }
    },
    [breedingNames]
  )

  const handleOnCultureNameChange = useCallback(
    (value, countryName) => setBreedingNames(prevState => ({
      ...prevState,
      [countryName]: value
    })),
    []
  )

  const handleSubmit = useCallback(
    ({ color, countries: dataCountries, ...data }) => {
      setLoading(true)

      const creations = {}

      forEach(
        dataCountries,
        (country, index) => {
          const { name } = data.creations[index]
          creations[countryFieldNames[getCountryIdByName(country)]] = name
        }
      )

      dispatch(BreedingActions.saveBreeding(
        {
          ...breeding,
          ...data,
          ...creations,
          color
        },
        () => {
          snackbar.success(t('{this} {action} successfully!', {
            this: t('breeding', { howMany: 1 }),
            action: toLower(t(isEmpty(breeding) ? 'created' : 'updated', { gender: 'female', howMany: 1 }))
          }))
          onSuccess()
        },
        onError
      ))
    },
    [breeding, dispatch, getCountryIdByName, onError, onSuccess, setLoading, snackbar, t]
  )

  useEffect(() => {
    const initialBreedingNames = {}

    breeding.ptBr && merge(initialBreedingNames, { 'Brazil': breeding.ptBr })
    breeding.deDe && merge(initialBreedingNames, { 'Germany': breeding.deDe })
    breeding.esEs && merge(initialBreedingNames, { 'Paraguay': breeding.esEs })
    breeding.enUs && merge(initialBreedingNames, { 'United States': breeding.enUs })

    setBreedingNames(initialBreedingNames)
  }, [breeding.deDe, breeding.enUs, breeding.esEs, breeding.ptBr])

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => breeding.referenceCreationId && setLoading(true), [])

  return (
    <Form
      ref={ formRef }
      schemaConstructor={ breedingSchema }
      onSubmit={ handleSubmit }
    >
      <Container>
        <InputText
          name="name"
          label={ t('reference name') }
          defaultValue={ breeding?.name ?? '' }
          disabled={ loading }
          fullWidth
        />

        <InputSelect
          name="countries"
          label={ t('country', { howMany: 1 }) }
          onFieldValueChange={ handleOnCountryChange }
          options={ countryOptions }
          defaultValue={ Object.keys(breedingNames) }
          disabled={ loading }
          fullWidth
          multiple
        />

        {map(Object.keys(breedingNames), (countryName, index) => (
          <Scope path={ `creations[${ index }]` } key={ countryName }>
            {/* <InputText
              name="name"
              label={ `${ t('name') } (${ countryName })` }
              onFieldValueChange={ value => handleOnCultureNameChange(value, countryName) }
              defaultValue={ breedingNames?.[countryName] }
              disabled={ loading }
              fullWidth
            /> */}
            <InputText
              name="name"
              label={ `${ t('name') } (${ t('country name by locale code', {
                localeCode: countryFieldNames[getCountryIdByName(countryName)] ?? 'enUs'
              }) })` }
              onFieldValueChange={ value => handleOnCultureNameChange(value, countryName) }
              defaultValue={ breedingNames?.[countryName] }
              disabled={ loading }
              fullWidth
            />
          </Scope>
        ))}

        <InputColor
          name="color"
          label={ t('color') }
          defaultValue={ breeding?.color ?? '' }
          fullWidth
        />
      </Container>
    </Form>
  )
})

BreedingForm.propTypes = {
  breeding: PropTypes.object,
  loading: PropTypes.bool,
  setLoading: PropTypes.func,
  onSuccess: PropTypes.func,
  onError: PropTypes.func
}

BreedingForm.defaultProps = {
  breeding: {},
  loading: false,
  setLoading: () => {},
  onSuccess: () => {},
  onError: () => {}
}

export default BreedingForm
